import type { PriorityExportRecord } from './integrationTypes';

/** Serialize a priority-like record to a flat row for CSV/Excel (all keys; dates and objects stringified). */
export function priorityToExportRow(p: PriorityExportRecord): Record<string, string | number> {
  const row: Record<string, string | number> = {};
  for (const key of Object.keys(p)) {
    const v = p[key];
    if (v === undefined || v === null) {
      row[key] = '';
    } else if (typeof v === 'object' && v !== null && typeof (v as Date).toISOString === 'function') {
      row[key] = (v as Date).toISOString();
    } else if (typeof v === 'object' && v !== null) {
      try {
        row[key] = JSON.stringify(v);
      } catch {
        row[key] = String(v);
      }
    } else if (typeof v === 'number' || typeof v === 'boolean') {
      row[key] = v as number;
    } else {
      row[key] = String(v);
    }
  }
  return row;
}

// Every column the Excel/CSV export writes, ordered by logical group (not alphabetical).
// A re-import reads only IMPORT_TEMPLATE_COLUMNS; the rest are for whoever reads the file.
export const EXPORT_COLUMNS = [
  // Identity & core
  'id',
  'title',
  'description',
  // Ranking & list context
  'rank',
  'aboveTheLine',
  'itemPoolId',
  'slottingRationale',
  'scores',
  // Lifecycle
  'lifecycle_state',
  'lifecycle_stage',
  'lifecycleState',
  'lifecycleStage',
  'detailedState',
  // Dates
  'createdAt',
  'updatedAt',
  'shapedAt',
  'shapingStartedAt',
  'leadershipApprovedAt',
  'deferredAt',
  'deferReviewDate',
  'routedAt',
  // People & ownership
  'createdBy',
  'createdByUserId',
  'shapingOwnerId',
  'stewardId',
  'leadershipApprovedBy',
  'routedBy',
  // Classification & framing
  'itemCategory',
  'metaCategory',
  'strategicBucket',
  'tags',
  'source',
  'beneficiary',
  'desiredOutcome',
  'evidenceOfSuccess',
  'whyNow',
  'obstacles',
  'unintendedConsequences',
  // Altitude
  'altitudeLevel',
  'altitude',
  'declaredAltitude',
  'canonicalAltitude',
  'altitudeLifecycleState',
  'altitudeScopingInputs',
  'altitudeCompletionRequirements',
  // Hierarchy & dedupe
  'parentItemId',
  'childItemIds',
  'dedupeStatus',
  'duplicateOfId',
  'mergedFromIds',
  // Shaping & attributes
  'readyForRanking',
  'shapingCompleteness',
  'attributeValues',
  'attributes',
  // Leadership & routing
  'leadershipApproved',
  'stewardshipBucketId',
  'queueId',
  'orgUnitId',
  'workspaceId',
  'routingReason',
  // Clarification & deferral
  'clarificationNeeded',
  'hasPendingClarifications',
  'deferReason',
  // OST & frames
  'ostNodeId',
  'ostOpportunityId',
  'frames',
  'canonicalFrameId',
  // Metadata
  'metadata',
] as const;

/**
 * The import template: only the columns a re-import reads (rowToImportItem,
 * readImportedRank). A column here that the import ignores is a promise the
 * import breaks; integrationMappers.test.ts checks each one is read.
 * `rank` applies only where the list allows it (RankImportOptions).
 */
export const IMPORT_TEMPLATE_COLUMNS = [
  'id',
  'title',
  'description',
  'rank',
  'itemCategory',
  'metaCategory',
  'strategicBucket',
  'altitudeLevel',
  'tags',
  'work_type',
] as const;

/** One example row: only title/description have hints; rest empty so template is ready to fill. */
export const IMPORT_TEMPLATE_EXAMPLE: Record<string, string> = Object.fromEntries(
  IMPORT_TEMPLATE_COLUMNS.map((h) => [
    h,
    h === 'title' ? 'Example initiative or idea' : h === 'description' ? 'Brief description; optional.' : '',
  ])
);

const VALID_ITEM_CATEGORIES = ['problem', 'opportunity', 'risk', 'solution', 'commitment', 'investment'] as const;
const VALID_META_CATEGORIES  = ['people', 'things', 'places', 'activities', 'information', 'time'] as const;
const VALID_STRATEGIC_BUCKETS = ['growth', 'commitments', 'reliability', 'platform', 'compliance'] as const;
const VALID_EXIT_STATUSES     = ['active', 'deferred', 'archived'] as const;
const VALID_ALTITUDES         = ['1', '2', '3', '4', '5'] as const;

export type ParsedRowExtended = {
  id: string;
  title: string;
  description?: string;
  source: string;
  externalId: string;
  // Additional fields for round-trip update
  tags?: string[];
  itemCategory?: string;
  metaCategory?: string;
  strategicBucket?: string;
  altitudeLevel?: number;
  exit_status?: string;
  work_type?: string;
};

/** The row's `rank` column as a positive number, or null when it has none. */
export function readImportedRank(row: Record<string, string | number | undefined>): number | null {
  const key = Object.keys(row).find((c) => c.toLowerCase().trim() === 'rank');
  if (key == null) return null;
  const raw = row[key];
  if (raw === undefined || raw === null || String(raw).trim() === '') return null;
  const n = Number(String(raw).trim());
  return Number.isFinite(n) && n > 0 ? n : null;
}

/**
 * The pool's order once a file's ranks are applied. Members the file ranks
 * sort by that rank; a member it leaves out keeps its current place as its
 * rank; ties keep the current order. Ids the pool does not hold are ignored.
 */
export function orderFromImportedRanks(currentIds: string[], ranks: ReadonlyMap<string, number>): string[] {
  return currentIds
    .map((id, index) => ({ id, index, rank: ranks.get(id) ?? index + 1 }))
    .sort((a, b) => a.rank - b.rank || a.index - b.index)
    .map((m) => m.id);
}

/** Map a row (CSV or Excel) to an import item; picks title/description by common column names.
 *  Also extracts id and metadata columns for round-trip update support. */
export function rowToImportItem(
  row: Record<string, string | number | undefined>,
  index: number,
  source: 'csv' | 'excel'
): ParsedRowExtended {
  const get = (keys: string[]): string => {
    for (const k of keys) {
      const key = Object.keys(row).find((c) => c.toLowerCase().trim() === k.toLowerCase());
      if (key != null) {
        const v = row[key];
        if (v !== undefined && v !== null && String(v).trim() !== '') return String(v).trim();
      }
    }
    return '';
  };
  const title = get(['title', 'name', 'summary', 'label', 'item', '0', 'column_0']) || `Item ${index + 1}`;
  const description = get(['description', 'body', 'details', 'notes', '1', 'column_1']) || undefined;
  const id = get(['id']);

  // Metadata columns (round-trip)
  const rawTags = get(['tags']);
  const tags = rawTags ? rawTags.split(/[|;,]/).map(t => t.trim()).filter(Boolean) : undefined;
  const rawCategory = get(['itemCategory', 'item_category', 'category']);
  const itemCategory = (VALID_ITEM_CATEGORIES as readonly string[]).includes(rawCategory) ? rawCategory : undefined;
  const rawMeta = get(['metaCategory', 'meta_category']);
  const metaCategory = (VALID_META_CATEGORIES as readonly string[]).includes(rawMeta) ? rawMeta : undefined;
  const rawBucket = get(['strategicBucket', 'strategic_bucket']);
  const strategicBucket = (VALID_STRATEGIC_BUCKETS as readonly string[]).includes(rawBucket) ? rawBucket : undefined;
  const rawAlt = get(['altitudeLevel', 'altitude_level', 'altitude']);
  const altitudeLevel = (VALID_ALTITUDES as readonly string[]).includes(rawAlt) ? parseInt(rawAlt, 10) : undefined;
  const rawExit = get(['exit_status', 'exitStatus', 'status']);
  const exit_status = (VALID_EXIT_STATUSES as readonly string[]).includes(rawExit) ? rawExit : undefined;
  const work_type = get(['work_type', 'workType', 'type']) || undefined;

  return {
    id,
    title,
    description: description || undefined,
    source,
    externalId: `row-${index}-${Date.now()}`,
    tags,
    itemCategory,
    metaCategory,
    strategicBucket,
    altitudeLevel,
    exit_status,
    work_type,
  };
}
